import React, { useState } from "react";
import axios from "axios";
import { Gift, Sparkles } from "lucide-react";
import { API, auth, TOK, SERIF, SANS, Title } from "./shared";

const Option = ({ testId, tag, title, price, sub, onClick, busy, strong }) => (
  <button type="button" onClick={onClick} disabled={busy} data-testid={testId}
    className="w-full text-left rounded-2xl px-5 py-4 transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
    style={{ background: strong ? "rgba(215,187,122,0.12)" : "rgba(245,236,217,0.04)", border: `1px solid ${strong ? TOK.gold : TOK.goldMist}` }}>
    {tag && <span className="text-[0.58rem] uppercase tracking-[0.28em]" style={{ color: TOK.gold, fontFamily: SANS }}>{tag}</span>}
    <span className="mt-1 flex items-baseline justify-between gap-3">
      <span className="text-xl" style={{ color: TOK.cream, fontFamily: SERIF }}>{title}</span>
      <span className="text-sm whitespace-nowrap" style={{ color: TOK.cream, fontFamily: SANS }}>{price}</span>
    </span>
    <span className="mt-1 block text-sm" style={{ color: TOK.creamMist, fontFamily: SANS }}>{sub}</span>
  </button>
);

// Coach-link clients who aren't members choose how this installation is covered.
export const CoachPassStep = ({ fromCoach, onCovered }) => {
  const [busy, setBusy] = useState("");
  const [annual, setAnnual] = useState(false);
  const [error, setError] = useState("");
  const pay = async (option) => {
    setBusy(option); setError("");
    try {
      const r = await axios.post(`${API}/api/soulumo/coach-prep/${fromCoach.id}/checkout`, { option, origin_url: window.location.origin }, auth());
      window.location.href = r.data.checkout_url;
    } catch { setError("Couldn't open the payment page just now. Please try again."); setBusy(""); }
  };
  if (fromCoach.access === "covered") {
    return (
      <div className="space-y-6" data-testid="coach-pass-gift">
        <Gift size={26} style={{ color: TOK.gold }} />
        <Title>{fromCoach.gift ? `A gift from ${fromCoach.coach_name}` : "You're all set"}</Title>
        <p className="text-lg italic" style={{ color: TOK.creamDim, fontFamily: SERIF }}>
          {fromCoach.gift ? "Your first personal installation is covered." : "This installation is paid for."} Now bring these words to life in your own voice.
        </p>
        <button type="button" onClick={onCovered} data-testid="coach-pass-continue" className="w-full rounded-full py-4 text-xs uppercase tracking-[0.26em] font-bold"
          style={{ background: TOK.gold, color: TOK.ink, fontFamily: SANS }}>Continue</button>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="coach-pass-step">
      <Sparkles size={24} style={{ color: TOK.gold }} />
      <Title>Bring these words to life in your own voice.</Title>
      <Option strong testId="coach-pass-start" tag="Most popular" title="Start Soulumo" busy={!!busy}
        price={annual ? "£60.99 / year" : <><s className="mr-1 opacity-60" data-testid="coach-pass-was-price">£9.99</s>£6.99 first month</>} onClick={() => pay(annual ? "annual" : "monthly")}
        sub={annual ? "Create this installation and unlock Soulumo for a year." : "Create this installation and unlock Soulumo. Then £6.99 a month, cancel any time."} />
      <button type="button" onClick={() => setAnnual((a) => !a)} className="text-xs underline" style={{ color: TOK.creamMist, fontFamily: SANS }} data-testid="coach-pass-annual-toggle">
        {annual ? "Prefer monthly at £6.99?" : "Or £60.99 for a whole year"}
      </button>
      <Option testId="coach-pass-one-off" title="Just this installation" price="£1.99" busy={!!busy} onClick={() => pay("one_off")}
        sub="Create and keep this installation. No subscription." />
      {busy && <p className="text-sm" style={{ color: TOK.creamMist }} data-testid="coach-pass-busy">Opening secure payment…</p>}
      {error && <p className="text-sm" style={{ color: "#e9a39c" }} data-testid="coach-pass-error">{error}</p>}
    </div>
  );
};
